import { cacheOptions, fetchFirebaseData, toList } from "../../../../connector/functions";
import { useQuery } from "@tanstack/react-query";

type SoftSkill = {
    name: string;
    level?: number;
    order?: number;
};

type SoftSkillsBlock = {
    title?: string;
    titleHighlight?: string;
    subtitle?: string;
    skills?: SoftSkill[];
};

type SkillsHookResult = {
    title?: string;
    titleHighlight?: string;
    subtitle?: string;
    softSkills?: SoftSkillsBlock;
    categories: any[];
    certifications: {
        title?: string;
        list?: unknown;
    };
};

export const useFirebaseSkillsHooks = (l: string): SkillsHookResult => {
    const skillsSection = useQuery({
        queryKey: ['skills', l],
        queryFn: () => fetchFirebaseData(`site/${l}/skillsSection`),
        ...cacheOptions,
    });

    const skillsStats = useQuery({
        queryKey: ['skillsStats'],
        queryFn: () => fetchFirebaseData(`site/shared/skills`),
        ...cacheOptions,
    });

    const certifications = useQuery({
        queryKey: ['certifications'],
        queryFn: () => fetchFirebaseData(`site/shared/certifications`),
        ...cacheOptions,
    });

    if (!skillsSection.data || !skillsStats.data) {
        return {
            title: undefined,
            titleHighlight: undefined,
            subtitle: undefined,
            softSkills: undefined,
            categories: [],
            certifications: {
                title: skillsSection.data?.certifications?.title,
                list: toList(certifications.data),
            },
        };
    }

    const allSkills: Record<string, any> = {
        ...skillsSection.data,
        ...skillsStats.data,
        categories: { ...(skillsSection.data.categories || {}) },
    };

    Object.keys(skillsSection.data.categories || {}).forEach((key: string) => {
        const category = skillsSection.data.categories[key];
        const stats = skillsStats.data?.[key];
        allSkills.categories[key] = {
            ...category,
            stats,
            order: stats?.order ?? 0,
        };
        delete allSkills[key];
    });

    let softSkills: SoftSkillsBlock | undefined = skillsSection.data.softSkills;
    if (skillsSection.data.softSkills && skillsStats.data.soft) {
        softSkills = {
            ...skillsSection.data.softSkills,
            skills: Object.keys(skillsStats.data.soft).map((key: string) => ({
                name: skillsStats.data.soft[key]?.name || key,
                level: skillsStats.data.soft[key]?.level,
                order: skillsStats.data.soft[key]?.order,
            })),
        };
        delete allSkills.soft;
    }

    return {
        title: allSkills.title,
        titleHighlight: allSkills.titleHighlight,
        subtitle: allSkills.subtitle,
        softSkills,
        categories: Object.values(allSkills.categories ?? {}).sort(
            (a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0),
        ),
        certifications: {
            ...(allSkills.certifications || {}),
            list: toList(certifications.data),
        },
    };
};
